import {
  Component,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  NgZone,
} from "@angular/core";
import { FileService } from "@app/services/file.service";
import { UserService } from "@app/services/user.service";

class Tag {
  title: string;
  keyword: string;
  isPresent: boolean;
  children: Tag[];

  constructor(title: string, keyword: string, children?: Tag[]) {
    this.title = title;
    this.keyword = keyword;
    this.isPresent = false;
    this.children = children || [];
  }
}

@Component({
  selector: "froala-tags-editor",
  templateUrl: "./froala-tags-editor.component.html",
})
export class FroalaTagsEditorComponent {
  @Input()
  metafieldsTags: string[] = [];

  htmlEditor: any;

  fonts: string[] = [
    "Amiri",
    "Droid Sans",
    "Kurale",
    "Happy Monkey",
    "Hind",
    "Lato",
    "Mallanna",
    "Montserrat",
    "Minion Pro",
    "Open Sans",
    "Overlock",
    "Sacramento",
    "Satisfy",
  ];
  froalaOptions: Object = {
    key: "RB3J4B17C10iB6E5E4H5I2I3A6B6C5C4C-11NGNe1IODMGYNSFKV==",
    toolbarButtons: [
      "fontFamily",
      "fontSize",
      "align",
      "color",
      "|",
      "bold",
      "italic",
      "underline",
      "|",
      "html",
    ],
    iconsTemplate: "font_awesome_5",
    colorsText: [
      "#e6292f",
      "#fd6721",
      "#fff64a",
      "#52d15d",
      "#81b5e5",
      "#4253bd",
      "#6d41a0",
      "#fdcdd5",
      "#b97957",
      "#777777",
      "#ffffff",
      "#000000",
    ],
    colorsBackground: [
      "#e6292f",
      "#fd6721",
      "#fff64a",
      "#52d15d",
      "#81b5e5",
      "#4253bd",
      "#6d41a0",
      "#fdcdd5",
      "#b97957",
      "#777777",
      "#ffffff",
      "#000000",
      "REMOVE",
    ],
    fontSizeSelection: true,
    fontSize: [
      "6",
      "8",
      "9",
      "10",
      "11",
      "12",
      "14",
      "18",
      "24",
      "36",
      "42",
      "64",
      "128",
    ],
    fontSizeUnit: "px",
    colorsStep: 6,
    lineHeights: {
      Default: "",
      Single: "1",
      "1.1": "1.1",
      "1.2": "1.2",
      "1.3": "1.3",
      "1.4": "1.4",
      "1.5": "1.5",
      "1.6": "1.6",
      "1.7": "1.7",
      "1.8": "1.8",
      Double: "2",
      "2.25": "2.25",
      "2.5": "2.5",
      "2.75": "2.75",
      Triple: "3",
    },
    immediateAngularModelUpdate: true,
    colorsHEXInput: true,
    charCounterCount: false,
    fontFamilySelection: true,
    imageAllowedTypes: ["jpeg", "jpg", "png"],
    imageMaxSize: 1024 * 1024 * 2, // 2 MB
    imageDefaultWidth: 20,
    imageDefaultDisplay: "inline",
    imageOutputSize: true,
    imageEditButtons: ["imageReplace", "imageAlign", "imageRemove"],
    imageInsertButtons: ["imageBack", "|", "imageUpload", "imageByURL"],
    imageStyles: {},
    placeholderText: "",
    tableColors: [
      "#e6292f",
      "#fd6721",
      "#fff64a",
      "#52d15d",
      "#81b5e5",
      "#4253bd",
      "#6d41a0",
      "#fdcdd5",
      "#b97957",
      "#777777",
      "#ffffff",
      "#000000",
    ],
    zIndex: 10000,
    imageUploadToS3: {
      bucket: "elasticbeanstalk-us-east-1-462591887108",
      // Your bucket region.
      region: "s3-us-east-1",
      keyStart: "uploads/",
      params: {
        acl: "public-read", // ACL according to Amazon Documentation.
        AWSAccessKeyId: "AKIAJ26WPZPOLYFRHDRA", // Access Key from Amazon.
        policy: "POLICY_STRING", // Policy string computed in the backend.
        signature: "", // Signature computed in the backend.
      },
    },
    tableStyles: {
      "fr-solid-borders": "Solid Borders",
    },
    tableCellStyles: {
      "fr-solid-borders": "Solid Borders",
    },
    height: 150,
    toolbarInline: true,
    toolbarVisibleWithoutSelection: true,
    events: {
      "froalaEditor.contentChanged": () => {
        this.zone.run(() => {
          this.checkFieldPresence();
        });
      },
      // 'froalaEditor.focus': () => {
      //   this.zone.run(() => {
      //     this.displayButtons = true;
      //   });
      // },
      // 'froalaEditor.blur': () => {
      //   this.zone.run(() => {
      //     this.displayButtons = false;
      //   });
      // }
    },
  };

  TEXT_TAGS: Tag[] = [
    new Tag("Logo", "[BUSINESS_LOGO]"),
    new Tag("Contact Information", "[BUSINESS_ADDRESS]", [
      new Tag("Business Name", "[BUSINESS_NAME]"),
      new Tag("Address", "[BUSINESS_ADDRESS]"),
      new Tag("Address 2", "[BUSINESS_ADDRESS2]"),
      new Tag("Website", "[BUSINESS_WEBSITE]"),
      new Tag("City", "[BUSINESS_CITY]"),
      new Tag("Zip Code", "[BUSINESS_ZIP_CODE]"),
      new Tag("State/Province", "[BUSINESS_STATE_PROVINCE]"),
      new Tag("Country", "[BUSINESS_COUNTRY]"),
      new Tag("Phone", "[BUSINESS_PHONE_NUMBER]"),
      new Tag("Contact", "[BUSINESS_CONTACT_EMAIL]"),
    ]),
    new Tag("Page", "[PAGE_NUMBER]"),
  ];

  LISTING_TAGS: Tag[] = [
    new Tag("Image", "[MAIN_IMAGE]", [
      new Tag("Image", "[MAIN_IMAGE]"),
      new Tag("Image (Same Size)", "[MAIN_IMAGE_COVER]"),
    ]),
    new Tag("SKU", "[SKU]"),
    new Tag("Title", "[TITLE]"),
    new Tag("Description", "[DESCRIPTION]"),
    new Tag("Currency", "[CURRENCY]"),
    new Tag("Price", "[PRICE]", [
      new Tag("Price", "[PRICE]"),
      new Tag("Truncated", "[PRICE(INT)]"),
      new Tag("Price Decimals", "[PRICE(DECIMAL)]"),
    ]),
    new Tag("MSRP", "[MSRP]", [
      new Tag("MSRP", "[MSRP]"),
      new Tag("Truncated", "[MSRP(INT)]"),
      new Tag("Decimals", "[MSRP(DECIMAL)]"),
    ]),
    new Tag("Compare Price", "[COMPARE_AT_PRICE]", [
      new Tag("Compare Price", "[COMPARE_AT_PRICE]"),
      new Tag("Truncated", "[COMPARE_AT_PRICE(INT)]"),
      new Tag("Decimals", "[COMPARE_AT_PRICE(DECIMAL)]"),
    ]),
    new Tag("Variants", "<div>[VARIANTS(TITLE, SKU, PRICE)]</div>", [
      new Tag("Variants", "<div>[VARIANTS(TITLE, SKU, PRICE)]</div>"),
      new Tag(
        "Variants (Table)",
        "<div>[VARIANTS(TITLE, SKU, PRICE, TABLE)]</div>"
      ),
    ]),
    new Tag("Option", "[VARIANT_OPTION_TITLE]"),
    new Tag("Barcode", "[BARCODE]", [
      new Tag("Number", "[BARCODE_DATA]"),
      new Tag("EAN 13", "[BARCODE_EAN13]"),
      new Tag("EAN 8", "[BARCODE_EAN8]"),
      new Tag("UPC-A", "[BARCODE_UPCA]"),
      new Tag("UPC-E", "[BARCODE_UPCE]"),
      new Tag("ITF-14", "[BARCODE_ITF14]"),
      new Tag("Code 39", "[BARCODE_39]"),
      new Tag("Code 128", "[BARCODE_128]"),
    ]),
    new Tag("QRCode", "[QRCODE]"),
    new Tag("Stock", "[STOCK]"),
  ];
  //  BARCODE_LISTING_TAGS: Tag[] = [
  //   new Tag('Image', '[MAIN_IMAGE]'),
  //   new Tag('SKU', '[SKU]'),
  //   new Tag('Title', '[TITLE]'),
  //   new Tag('Description', '[DESCRIPTION]'),
  //   new Tag('Currency', '[CURRENCY]'),
  //   new Tag('Price', '[PRICE]', [
  //     new Tag('Price', '[PRICE]'),
  //     new Tag('Truncated', '[PRICE(INT)]'),
  //     new Tag('Price Decimals', '[PRICE(DECIMAL)]'),
  //   ]),
  //   new Tag('MSRP', '[MSRP]', [
  //     new Tag('MSRP', '[MSRP]'),
  //     new Tag('Truncated', '[MSRP(INT)]'),
  //     new Tag('Decimals', '[MSRP(DECIMAL)]')
  //   ]),
  //   new Tag('Variant', '[VARIANT]'),
  //   new Tag('QRCode', '[QRCODE]'),
  //   new Tag('Stock', '[STOCK]')
  // ];
  tags: Tag[];
  @Input()
  public type: String;
  @Input()
  public _html: String;
  @Input()
  public name: String;

  @Output() htmlChange = new EventEmitter();

  froalaLoaded: boolean = false;
  displayButtons: boolean = true;

  constructor(
    private userService: UserService,
    private fileService: FileService,
    private zone: NgZone
  ) {
    this.fileService.get("font").then((customFonts) => {
      customFonts.forEach((customFont) => {
        this.fonts.push(customFont.name);
      });
      this.fonts.sort();
      var froalaFonts = {};
      this.fonts.forEach((font) => {
        froalaFonts[`'${font}'`] = font;
      });
      this.froalaOptions["fontFamily"] = froalaFonts;
      if (this.type === "text") {
        this.tags = this.TEXT_TAGS;
        this.froalaOptions["toolbarButtons"] = [
          "fontFamily",
          "fontSize",
          "lineHeight",
          "align",
          "color",
          "|",
          "bold",
          "italic",
          "underline",
          "strikeThrough",
          "|",
          "formatOL",
          "formatUL",
          "|",
          "specialCharacters",
          "|",
          "insertImage",
          "insertTable",
          "insertHR",
          "|",
          "undo",
          "redo",
          "|",
          "clearFormatting",
          "html",
        ];
        // this.froalaOptions['toolbarInline'] = false;
      } else if (this.type === "listing") {
        this.tags = this.LISTING_TAGS;
        this.froalaOptions["toolbarButtons"] = [
          "fontFamily",
          "fontSize",
          "lineHeight",
          "align",
          "color",
          "|",
          "bold",
          "italic",
          "underline",
          "|",
          "html",
        ];
        // this.froalaOptions['toolbarInline'] = false;
      }
      // else if (this.type === 'barcode-listing') {
      //   this.tags = this.BARCODE_LISTING_TAGS;
      //   this.froalaOptions['toolbarButtons'] = ['fontFamily', 'fontSize', 'lineHeight', 'align', 'color',
      //   '|', 'bold', 'italic', 'underline',
      //   '|', 'html'];
      //   // this.froalaOptions['toolbarInline'] = false;
      // }
      this.userService.me().then((user) => {});
      this.fileService.getS3Config().then((s3Config) => {
        this.froalaOptions["imageUploadToS3"] = s3Config;
        this.froalaLoaded = true;
        this.checkFieldPresence();
      });
    });
  }

  switchField($event, tag) {
    if (tag.isPresent) {
      this.html = this.html.replace(tag.keyword, "");
    } else {
      this.htmlEditor("html.insert", tag.keyword);
      this.html = this.htmlEditor("html.get");
    }
    this.checkFieldPresence();
    $event.preventDefault();
    $event.stopPropagation();
  }

  @Input()
  get html() {
    return this._html;
  }

  set html(val) {
    this._html = val;
    this.htmlChange.emit(this._html);
  }

  checkFieldPresence() {
    if (this.html && this.tags) {
      var html = this.html;
      this.tags.forEach((tag) => {
        tag.isPresent = html.indexOf(tag.keyword) > -1;
        tag.children.forEach(function (subtag) {
          subtag.isPresent = html.indexOf(subtag.keyword) > -1;
        });
      });
    }
  }

  initializeFroala(initControls) {
    this.htmlEditor = initControls.getEditor();
    initControls.initialize();
    this.checkFieldPresence();
  }
}
